import React from 'react';
import { func, number, array, bool, string, node } from 'prop-types';
import {
  Button,
  ButtonGroup,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Avatar,
  Badge,
} from '@mui/material';
import { I18n } from 'utils/i18n';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import initialsFromUsername from 'utils/initialsFromUsername';
import { getColor } from './signerColors';

const FieldToolbar = ({
  onAddField,
  showSignerSelector = false,
  currentSignerIndex = 0,
  onSignerChange = () => {},
  members = [],
  signingMode = 'onePerMember',
  onSigningModeChange = () => {},
  showSigningMode = false,
  fields = [],
  pageControls = null,
}) => {
  const fieldTypes = [
    {
      type: 'signature',
      icon: DriveFileRenameOutlineOutlinedIcon,
      label: I18n.t('Fields.Signature'),
    },
    {
      type: 'checkbox',
      icon: CheckBoxOutlinedIcon,
      label: I18n.t('Fields.Checkbox'),
    },
    {
      type: 'date',
      icon: CalendarTodayOutlinedIcon,
      label: I18n.t('Fields.Date'),
    },
    {
      type: 'text',
      icon: TextFieldsOutlinedIcon,
      label: I18n.t('Fields.Text'),
    },
  ];

  // Calculate missing signatures indicator
  const getMissingSignaturesCount = () => {
    const mandatorySignatureFields = fields.filter(
      field => field.type === 'signature' && field.mandatory === true,
    );

    if (signingMode === 'oneForAll') {
      // Each signer must have at least one mandatory signature field
      const numSigners = members?.length || 0;
      const signersWithFields = new Set(mandatorySignatureFields.map(field => field.signerIndex));
      return numSigners - signersWithFields.size;
    }

    // For onePerMember mode, just need at least one mandatory signature field
    return mandatorySignatureFields.length > 0 ? 0 : 1;
  };

  const missingCount = getMissingSignaturesCount();
  const tooltipText =
    signingMode === 'oneForAll'
      ? I18n.t('AcknowledgementCreate.SignatureIndicator.signersNeedFields', {
          count: missingCount,
        })
      : I18n.t('AcknowledgementCreate.SignatureIndicator.addMandatoryField');

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <ButtonGroup variant="outlined">
        {fieldTypes.map(({ type, icon: Icon, label }) => {
          const isSignatureButton = type === 'signature';
          // In oneForAll mode, only signature fields are allowed
          const isDisabled = false; // signingMode === 'oneForAll' && !isSignatureButton;

          const button = (
            <Button color="secondary" onClick={() => onAddField(type)} disabled={isDisabled}>
              <Icon />
            </Button>
          );

          // Wrap signature button with badge if there are missing signatures
          if (isSignatureButton && missingCount > 0) {
            return (
              <Tooltip key={type} title={tooltipText}>
                <Badge badgeContent={missingCount} color="warning">
                  {button}
                </Badge>
              </Tooltip>
            );
          }

          return (
            <Tooltip key={type} title={label}>
              {button}
            </Tooltip>
          );
        })}
      </ButtonGroup>

      {pageControls}

      {showSigningMode && (
        <FormControl>
          <InputLabel id="signing-mode-label">
            {I18n.t('AcknowledgementCreate.SigningMode.label')}
          </InputLabel>
          <Select
            sx={{ minWidth: 200, minHeight: '44px' }}
            labelId="signing-mode-label"
            value={signingMode}
            label={I18n.t('AcknowledgementCreate.SigningMode.label')}
            onChange={e => onSigningModeChange(e.target.value)}
          >
            <MenuItem value="onePerMember">
              {I18n.t('AcknowledgementCreate.SigningMode.onePerMember')}
            </MenuItem>
            <MenuItem value="oneForAll" disabled={members.length > 50}>
              {I18n.t('AcknowledgementCreate.SigningMode.oneForAll')}
              {members.length > 50 &&
                ` (${I18n.t('AcknowledgementCreate.SigningMode.tooManyMembers')})`}
            </MenuItem>
          </Select>
        </FormControl>
      )}

      {showSignerSelector && members.length > 0 && (
        <FormControl>
          <InputLabel id="signer-select-label">
            {I18n.t('AcknowledgementCreate.SignerSelector.label')}
          </InputLabel>
          <Select
            sx={{ minWidth: 200, minHeight: '44px' }}
            labelId="signer-select-label"
            value={currentSignerIndex}
            label={I18n.t('AcknowledgementCreate.SignerSelector.label')}
            onChange={e => onSignerChange(e.target.value)}
          >
            {members.map((member, index) => {
              const color = getColor(index);
              return (
                <MenuItem key={member._id || index} value={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="span"
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        mr: 1,
                        display: 'inline-block',
                        backgroundColor: color.text,
                      }}
                    />
                    <Avatar sx={{ width: 24, height: 24 }} src={member.avatar} alt={member.name}>
                      {initialsFromUsername(member.name)}
                    </Avatar>
                    <span>{member.name}</span>
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

FieldToolbar.propTypes = {
  onAddField: func.isRequired,
  showSignerSelector: bool,
  currentSignerIndex: number,
  onSignerChange: func,
  members: array,
  signingMode: string,
  onSigningModeChange: func,
  showSigningMode: bool,
  fields: array,
  pageControls: node,
};

export default FieldToolbar;
